/**
 * Session Report Generator (Browser-compatible)
 * Generates professional engineering reports in DOCX format
 * Uses monospace fonts for technical/engineering aesthetic
 * 
 * Usage:
 *   import { generateSessionReport, downloadReport } from './reportGenerator';
 *   
 *   const blob = await generateSessionReport({ session, project, ... });
 *   downloadReport(blob, 'report.docx');
 * 
 * NOTE: This is browser-only. No Node.js dependencies (fs, path, etc.)
 */

import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  Footer, AlignmentType, BorderStyle, WidthType, PageNumber,
  HeadingLevel, ShadingType, PageBreak
} from 'docx';

// ============================================================================
// CONSTANTS & STYLING
// ============================================================================

const MONO_FONT = 'Courier New';
const FONT_SIZES = {
  title: 32,      // 16pt
  heading1: 24,   // 12pt
  heading2: 22,   // 11pt
  normal: 20,     // 10pt
  small: 18,      // 9pt
  tiny: 16        // 8pt
};

const COLORS = {
  black: '000000',
  darkGray: '333333',
  gray: '666666',
  lightGray: 'CCCCCC',
  headerBg: 'E8E8E8',
  failBg: 'FFE8E8',
  passBg: 'E8FFE8',
  warnBg: 'FFF8E8'
};

const tableBorder = { style: BorderStyle.SINGLE, size: 1, color: COLORS.lightGray };
const cellBorders = { top: tableBorder, bottom: tableBorder, left: tableBorder, right: tableBorder };

// Page width in DXA (Letter size with 0.7" margins)
const PAGE_WIDTH = 9936;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format station from feet to engineering notation (XX+XX.XX)
 */
function formatStation(feet) {
  if (feet === null || feet === undefined) return '--';
  const stations = Math.floor(feet / 100);
  const remainder = (feet % 100).toFixed(2);
  return `${stations}+${remainder.padStart(5, '0')}`;
}

/**
 * Format distance in feet to miles or feet string
 */
function formatDistance(feet) {
  if (!feet) return '--';
  if (feet >= 5280) {
    return `${(feet / 5280).toFixed(2)} mi`;
  }
  return `${Math.round(feet).toLocaleString()} ft`;
}

/**
 * Format date to MM/DD/YYYY
 */
function formatDate(dateString) {
  if (!dateString) return '--';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '--';
  }
}

/**
 * Format number with fixed decimals
 */
function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined || isNaN(num)) return '--';
  return Number(num).toFixed(decimals);
}

/**
 * Create a text run with mono font styling
 */
function text(content, options = {}) {
  return new TextRun({
    text: String(content ?? '--'),
    font: MONO_FONT,
    size: options.size || FONT_SIZES.normal,
    bold: options.bold || false,
    color: options.color || COLORS.black,
    ...options
  });
}

/**
 * Create a paragraph with consistent styling
 */
function para(children, options = {}) {
  const childArray = Array.isArray(children) ? children : [children];
  return new Paragraph({
    spacing: { before: options.spaceBefore || 0, after: options.spaceAfter || 40 },
    alignment: options.alignment || AlignmentType.LEFT,
    children: childArray.map(c => typeof c === 'string' ? text(c) : c),
    ...options
  });
}

/**
 * Create a header cell for tables
 */
function headerCell(content, width, alignment = AlignmentType.LEFT) {
  return new TableCell({
    borders: cellBorders,
    width: { size: width, type: WidthType.DXA },
    shading: { fill: COLORS.headerBg, type: ShadingType.CLEAR },
    children: [new Paragraph({
      alignment,
      children: [text(content, { bold: true, size: FONT_SIZES.small })]
    })]
  });
}

/**
 * Create a data cell for tables
 */
function dataCell(content, width, options = {}) {
  const bgColor = options.status === 'fail' ? COLORS.failBg :
                  options.status === 'pass' ? COLORS.passBg :
                  options.status === 'warn' ? COLORS.warnBg : undefined;
  
  return new TableCell({
    borders: cellBorders,
    width: { size: width, type: WidthType.DXA },
    shading: bgColor ? { fill: bgColor, type: ShadingType.CLEAR } : undefined,
    children: [new Paragraph({
      alignment: options.alignment || AlignmentType.LEFT,
      children: [text(String(content ?? '--'), { size: FONT_SIZES.small, bold: options.bold })]
    })]
  });
}

/**
 * Create section heading
 */
function sectionHeading(title, level = 1) {
  return new Paragraph({
    heading: level === 1 ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2,
    spacing: { before: 120, after: 40 },
    children: [text(title, { 
      bold: true, 
      size: level === 1 ? FONT_SIZES.heading1 : FONT_SIZES.heading2,
      color: COLORS.darkGray
    })]
  });
}

/**
 * Create a key-value row for metadata display
 */
function keyValueRow(key, value) {
  return para([
    text(`${key}: `, { bold: true, size: FONT_SIZES.small }),
    text(value || '--', { size: FONT_SIZES.small })
  ], { spaceAfter: 40 });
}

/**
 * Create horizontal rule
 */
function horizontalRule() {
  return new Paragraph({
    border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.lightGray } },
    spacing: { before: 100, after: 100 },
    children: []
  });
}

// ============================================================================
// REPORT SECTIONS
// ============================================================================

/**
 * Generate report header section
 */
function generateHeader(session, project, projectConfig) {
  const meta = session?.meta || {};
  
  return [
    // Title
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [text('SESSION ANALYSIS REPORT', { bold: true, size: FONT_SIZES.title })]
    }),
    
    horizontalRule(),
    
    // Session Info
    para([text(session?.name || session?.filename || 'Unnamed Session', { bold: true, size: FONT_SIZES.heading2 })], 
      { alignment: AlignmentType.CENTER }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] }),
    
    // Two-column metadata
    new Table({
      columnWidths: [PAGE_WIDTH / 2, PAGE_WIDTH / 2],
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: cellBorders,
              width: { size: PAGE_WIDTH / 2, type: WidthType.DXA },
              children: [
                keyValueRow('Project', project?.name),
                keyValueRow('Operator', session?.operator || meta.operator),
                keyValueRow('Company', meta.company),
                keyValueRow('Run Date', formatDate(session?.run_date || meta.run_date)),
              ]
            }),
            new TableCell({
              borders: cellBorders,
              width: { size: PAGE_WIDTH / 2, type: WidthType.DXA },
              children: [
                keyValueRow('Profiler Model', session?.profiler_model || meta.model_num),
                keyValueRow('Serial Number', meta.serial_num),
                keyValueRow('Certification #', meta.certification_num),
                keyValueRow('Cert. Expiry', meta.certification_date),
              ]
            })
          ]
        })
      ]
    }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] })
  ];
}

/**
 * Generate collection settings section
 */
function generateSettingsSection(session, projectConfig) {
  const meta = session?.meta || {};
  const colWidths = [PAGE_WIDTH / 3, PAGE_WIDTH / 3, PAGE_WIDTH / 3];
  
  return [
    sectionHeading('COLLECTION SETTINGS'),
    
    new Table({
      columnWidths: colWidths,
      rows: [
        new TableRow({
          children: [
            headerCell('Parameter', colWidths[0]),
            headerCell('Value', colWidths[1]),
            headerCell('Units', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('IRI Threshold (Type A)', colWidths[0]),
            dataCell(formatNumber(projectConfig?.iri_threshold_type_a, 2), colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('in/mi', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('IRI Threshold (Type B)', colWidths[0]),
            dataCell(formatNumber(projectConfig?.iri_threshold_type_b, 2), colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('in/mi', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('Reduction Length', colWidths[0]),
            dataCell(meta['reduction_length(ft.)'] || '528', colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('ft', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('High-Pass Filter', colWidths[0]),
            dataCell(meta['high-pass_filter(ft.)'] || '--', colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('ft', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('Low-Pass Filter', colWidths[0]),
            dataCell(meta['low-pass_filter(ft.)'] || '--', colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('ft', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('Sample Rate', colWidths[0]),
            dataCell(meta.sample_rate || '--', colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('', colWidths[2])
          ]
        }),
        new TableRow({
          children: [
            dataCell('Collection Speed', colWidths[0]),
            dataCell(meta['collection_speed(mph)'] || '--', colWidths[1], { alignment: AlignmentType.RIGHT }),
            dataCell('mph', colWidths[2])
          ]
        })
      ]
    }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] })
  ];
}

/**
 * Generate session summary section
 */
function generateSummarySection(session, segments, events, projectConfig, isReversed = false) {
  let startStation = session?.analysis_start_station || session?.start_station_ft || 0;
  let endStation = session?.analysis_end_station || session?.end_station_ft || 0;
  
  // Swap start/end for reverse stationing
  if (isReversed) {
    [startStation, endStation] = [endStation, startStation];
  }
  
  const totalDistance = Math.abs(endStation - startStation);
  
  // Calculate IRI statistics
  const iriValues = (segments || [])
    .filter(s => s.iri_avg_in_per_mi != null)
    .map(s => s.iri_avg_in_per_mi);
  
  const avgIRI = iriValues.length > 0 
    ? iriValues.reduce((a, b) => a + b, 0) / iriValues.length 
    : null;
  
  const maxIRI = iriValues.length > 0 ? Math.max(...iriValues) : null;
  const minIRI = iriValues.length > 0 ? Math.min(...iriValues) : null;
  
  // Count events by type
  const allEvents = events || [];
  const lrEvents = allEvents.filter(e => e.event_type === 'LR');
  const bumpEvents = allEvents.filter(e => e.event_type === 'Bump');
  const dipEvents = allEvents.filter(e => e.event_type === 'Dip');
  
  // Count threshold exceedances
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const segmentCount = (segments || []).length;
  const exceedingSegments = (segments || []).filter(s => 
    s.iri_avg_in_per_mi && s.iri_avg_in_per_mi > threshold
  ).length;
  
  const halfWidth = PAGE_WIDTH / 2;
  const thirdWidth = PAGE_WIDTH / 3;
  
  return [
    sectionHeading('SESSION SUMMARY'),
    
    // Station range
    new Table({
      columnWidths: [halfWidth, halfWidth],
      rows: [
        new TableRow({
          children: [
            headerCell('Start Station', halfWidth),
            headerCell('End Station', halfWidth)
          ]
        }),
        new TableRow({
          children: [
            dataCell(formatStation(startStation), halfWidth, { alignment: AlignmentType.CENTER, bold: true }),
            dataCell(formatStation(endStation), halfWidth, { alignment: AlignmentType.CENTER, bold: true })
          ]
        })
      ]
    }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] }),
    
    // Statistics table
    new Table({
      columnWidths: [halfWidth, halfWidth],
      rows: [
        new TableRow({
          children: [
            headerCell('Metric', halfWidth),
            headerCell('Value', halfWidth)
          ]
        }),
        new TableRow({
          children: [
            dataCell('Total Distance', halfWidth),
            dataCell(formatDistance(totalDistance), halfWidth, { alignment: AlignmentType.RIGHT })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Total Segments', halfWidth),
            dataCell(segmentCount.toLocaleString(), halfWidth, { alignment: AlignmentType.RIGHT })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Average IRI', halfWidth),
            dataCell(`${formatNumber(avgIRI)} in/mi`, halfWidth, { alignment: AlignmentType.RIGHT })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Maximum IRI', halfWidth),
            dataCell(`${formatNumber(maxIRI)} in/mi`, halfWidth, { alignment: AlignmentType.RIGHT })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Minimum IRI', halfWidth),
            dataCell(`${formatNumber(minIRI)} in/mi`, halfWidth, { alignment: AlignmentType.RIGHT })
          ]
        }),
        new TableRow({
          children: [
            dataCell(`Segments Exceeding ${threshold} in/mi`, halfWidth),
            dataCell(
              segmentCount > 0 
                ? `${exceedingSegments} (${((exceedingSegments / segmentCount) * 100).toFixed(1)}%)`
                : '0',
              halfWidth, 
              { 
                alignment: AlignmentType.RIGHT,
                status: exceedingSegments > 0 ? 'warn' : 'pass'
              }
            )
          ]
        })
      ]
    }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] }),
    
    // Events summary
    new Table({
      columnWidths: [thirdWidth, thirdWidth, thirdWidth],
      rows: [
        new TableRow({
          children: [
            headerCell('Event Type', thirdWidth),
            headerCell('Count', thirdWidth),
            headerCell('Status', thirdWidth)
          ]
        }),
        new TableRow({
          children: [
            dataCell('Localized Roughness (ALR)', thirdWidth),
            dataCell(lrEvents.length.toString(), thirdWidth, { alignment: AlignmentType.CENTER }),
            dataCell(lrEvents.length > 0 ? 'ATTENTION' : 'NONE', thirdWidth, { 
              alignment: AlignmentType.CENTER,
              status: lrEvents.length > 0 ? 'warn' : 'pass'
            })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Bumps', thirdWidth),
            dataCell(bumpEvents.length.toString(), thirdWidth, { alignment: AlignmentType.CENTER }),
            dataCell(bumpEvents.length > 0 ? 'DETECTED' : 'NONE', thirdWidth, { alignment: AlignmentType.CENTER })
          ]
        }),
        new TableRow({
          children: [
            dataCell('Dips', thirdWidth),
            dataCell(dipEvents.length.toString(), thirdWidth, { alignment: AlignmentType.CENTER }),
            dataCell(dipEvents.length > 0 ? 'DETECTED' : 'NONE', thirdWidth, { alignment: AlignmentType.CENTER })
          ]
        })
      ]
    }),
    
    new Paragraph({ spacing: { after: 40 }, children: [] })
  ];
}

/**
 * Generate Rolling Straightedge section (Bumps & Dips)
 */
function generateRollingStraitedgeSection(events, isReversed = false) {
  const allEvents = events || [];
  const sortFn = (a, b) => isReversed 
    ? b.from_station_ft - a.from_station_ft 
    : a.from_station_ft - b.from_station_ft;
  const bumps = allEvents.filter(e => e.event_type === 'Bump').sort(sortFn);
  const dips = allEvents.filter(e => e.event_type === 'Dip').sort(sortFn);
  
  const colWidths = [PAGE_WIDTH * 0.35, PAGE_WIDTH * 0.25, PAGE_WIDTH * 0.2, PAGE_WIDTH * 0.2];
  const children = [
    sectionHeading('ROLLING STRAIGHTEDGE EVENTS'),
  ];
  
  // Bumps section
  children.push(
    para([text('BUMPS', { bold: true, size: FONT_SIZES.heading2 })], { spaceBefore: 20 })
  );
  
  if (bumps.length === 0) {
    children.push(para([text('No bumps detected.', { color: COLORS.gray })]));
  } else {
    const bumpRows = [
      new TableRow({
        children: [
          headerCell('Location', colWidths[0]),
          headerCell('Wheelpath', colWidths[1]),
          headerCell('Height (in)', colWidths[2]),
          headerCell('Peak Station', colWidths[3])
        ]
      }),
      ...bumps.slice(0, 50).map(bump => {
        // Swap from/to when reversed
        const location = isReversed ? bump.to_station_ft : bump.from_station_ft;
        return new TableRow({
          children: [
            dataCell(formatStation(location), colWidths[0]),
            dataCell(bump.wheelpath || '--', colWidths[1]),
            dataCell(formatNumber(bump.magnitude_in, 3), colWidths[2], { alignment: AlignmentType.RIGHT }),
            dataCell(formatStation(bump.peak_station_ft), colWidths[3])
          ]
        });
      })
    ];
    
    children.push(new Table({ columnWidths: colWidths, rows: bumpRows }));
    
    if (bumps.length > 50) {
      children.push(para([text(`... and ${bumps.length - 50} more bumps`, { color: COLORS.gray, size: FONT_SIZES.small })]));
    }
  }
  
  // Dips section
  children.push(
    para([text('DIPS', { bold: true, size: FONT_SIZES.heading2 })], { spaceBefore: 60 })
  );
  
  if (dips.length === 0) {
    children.push(para([text('No dips detected.', { color: COLORS.gray })]));
  } else {
    const dipRows = [
      new TableRow({
        children: [
          headerCell('Location', colWidths[0]),
          headerCell('Wheelpath', colWidths[1]),
          headerCell('Depth (in)', colWidths[2]),
          headerCell('Peak Station', colWidths[3])
        ]
      }),
      ...dips.slice(0, 50).map(dip => {
        // Swap from/to when reversed
        const location = isReversed ? dip.to_station_ft : dip.from_station_ft;
        return new TableRow({
          children: [
            dataCell(formatStation(location), colWidths[0]),
            dataCell(dip.wheelpath || '--', colWidths[1]),
            dataCell(formatNumber(dip.magnitude_in, 3), colWidths[2], { alignment: AlignmentType.RIGHT }),
            dataCell(formatStation(dip.peak_station_ft), colWidths[3])
          ]
        });
      })
    ];
    
    children.push(new Table({ columnWidths: colWidths, rows: dipRows }));
    
    if (dips.length > 50) {
      children.push(para([text(`... and ${dips.length - 50} more dips`, { color: COLORS.gray, size: FONT_SIZES.small })]));
    }
  }
  
  children.push(new Paragraph({ spacing: { after: 40 }, children: [] }));
  
  return children;
}

/**
 * Generate IRI Intervals section
 */
function generateIRIIntervalsSection(segments, projectConfig, isReversed = false) {
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const sortedSegments = [...(segments || [])].sort((a, b) => 
    isReversed 
      ? b.from_station_ft - a.from_station_ft  // Descending for reverse
      : a.from_station_ft - b.from_station_ft  // Ascending for normal
  );
  
  const children = [
    new Paragraph({ children: [new PageBreak()] }),
    sectionHeading('IRI INTERVALS'),
    para([text(`Threshold: ${threshold} in/mi`, { bold: true })]),
    new Paragraph({ spacing: { after: 40 }, children: [] })
  ];
  
  const colWidths = [
    PAGE_WIDTH * 0.17,  // Start Station
    PAGE_WIDTH * 0.17,  // End Station
    PAGE_WIDTH * 0.14,  // Length
    PAGE_WIDTH * 0.17,  // Left IRI
    PAGE_WIDTH * 0.17,  // Right IRI
    PAGE_WIDTH * 0.18   // MRI
  ];
  
  const headerRow = new TableRow({
    children: [
      headerCell('Start Station', colWidths[0]),
      headerCell('End Station', colWidths[1]),
      headerCell('Length', colWidths[2]),
      headerCell('Left IRI', colWidths[3]),
      headerCell('Right IRI', colWidths[4]),
      headerCell('MRI', colWidths[5])
    ]
  });
  
  // Limit to reasonable number for document
  const displaySegments = sortedSegments.slice(0, 100);
  
  const dataRows = displaySegments.map(seg => {
    const mri = seg.iri_avg_in_per_mi;
    
    // Swap from/to when reversed
    const startSta = isReversed ? seg.to_station_ft : seg.from_station_ft;
    const endSta = isReversed ? seg.from_station_ft : seg.to_station_ft;
    
    return new TableRow({
      children: [
        dataCell(formatStation(startSta), colWidths[0]),
        dataCell(formatStation(endSta), colWidths[1]),
        dataCell(formatDistance(seg.distance_ft), colWidths[2], { alignment: AlignmentType.RIGHT }),
        dataCell(formatNumber(seg.iri_left_in_per_mi), colWidths[3], { alignment: AlignmentType.RIGHT }),
        dataCell(formatNumber(seg.iri_right_in_per_mi), colWidths[4], { alignment: AlignmentType.RIGHT }),
        dataCell(formatNumber(mri), colWidths[5], { alignment: AlignmentType.RIGHT })
      ]
    });
  });
  
  children.push(new Table({ columnWidths: colWidths, rows: [headerRow, ...dataRows] }));
  
  if (sortedSegments.length > 100) {
    children.push(
      para([text(`Showing first 100 of ${sortedSegments.length} segments`, { color: COLORS.gray, size: FONT_SIZES.small })])
    );
  }
  
  children.push(new Paragraph({ spacing: { after: 40 }, children: [] }));
  
  return children;
}

/**
 * Generate Localized Roughness (ALR) section
 */
function generateALRSection(events, projectConfig, isReversed = false) {
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const lrEvents = (events || [])
    .filter(e => e.event_type === 'LR')
    .sort((a, b) => 
      isReversed
        ? b.from_station_ft - a.from_station_ft  // Descending for reverse
        : a.from_station_ft - b.from_station_ft  // Ascending for normal
    );
  
  const children = [
    new Paragraph({ children: [new PageBreak()] }),
    sectionHeading('LOCALIZED ROUGHNESS (ALR) EVENTS'),
    para([text(`Events exceeding IRI threshold of ${threshold} in/mi`, { color: COLORS.gray })]),
    new Paragraph({ spacing: { after: 40 }, children: [] })
  ];
  
  if (lrEvents.length === 0) {
    children.push(
      para([text('No localized roughness events detected.', { color: COLORS.gray })], { spaceBefore: 20 })
    );
  } else {
    // Summary stats
    const magnitudes = lrEvents.map(e => e.magnitude_in).filter(m => m != null);
    const avgMagnitude = magnitudes.length > 0 
      ? magnitudes.reduce((a, b) => a + b, 0) / magnitudes.length 
      : null;
    const maxMagnitude = magnitudes.length > 0 ? Math.max(...magnitudes) : null;
    
    children.push(
      para([
        text('Total ALR Events: ', { bold: true }),
        text(`${lrEvents.length}    `),
        text('Average Magnitude: ', { bold: true }),
        text(`${formatNumber(avgMagnitude)} in/mi    `),
        text('Maximum: ', { bold: true }),
        text(`${formatNumber(maxMagnitude)} in/mi`)
      ])
    );
    
    children.push(new Paragraph({ spacing: { after: 40 }, children: [] }));
    
    const colWidths = [
      PAGE_WIDTH * 0.20,  // From Station
      PAGE_WIDTH * 0.20,  // To Station
      PAGE_WIDTH * 0.15,  // Wheelpath
      PAGE_WIDTH * 0.20,  // IRI
      PAGE_WIDTH * 0.25   // Exceeds By
    ];
    
    const headerRow = new TableRow({
      children: [
        headerCell('From Station', colWidths[0]),
        headerCell('To Station', colWidths[1]),
        headerCell('Wheelpath', colWidths[2]),
        headerCell('IRI (in/mi)', colWidths[3]),
        headerCell('Exceeds By', colWidths[4])
      ]
    });
    
    const displayEvents = lrEvents.slice(0, 75);
    
    const dataRows = displayEvents.map(evt => {
      const exceedsBy = evt.magnitude_in ? evt.magnitude_in - threshold : null;
      
      // Swap from/to when reversed
      const fromSta = isReversed ? evt.to_station_ft : evt.from_station_ft;
      const toSta = isReversed ? evt.from_station_ft : evt.to_station_ft;
      
      return new TableRow({
        children: [
          dataCell(formatStation(fromSta), colWidths[0]),
          dataCell(formatStation(toSta), colWidths[1]),
          dataCell(evt.wheelpath || '--', colWidths[2], { alignment: AlignmentType.CENTER }),
          dataCell(formatNumber(evt.magnitude_in), colWidths[3], { alignment: AlignmentType.RIGHT, bold: true }),
          dataCell(exceedsBy > 0 ? `+${formatNumber(exceedsBy)}` : '--', colWidths[4], { 
            alignment: AlignmentType.RIGHT,
            status: exceedsBy > 100 ? 'fail' : 'warn'
          })
        ]
      });
    });
    
    children.push(new Table({ columnWidths: colWidths, rows: [headerRow, ...dataRows] }));
    
    if (lrEvents.length > 75) {
      children.push(
        para([text(`Showing first 75 of ${lrEvents.length} ALR events`, { color: COLORS.gray, size: FONT_SIZES.small })])
      );
    }
  }
  
  children.push(new Paragraph({ spacing: { after: 40 }, children: [] }));
  
  return children;
}

/**
 * Generate report footer
 */
function generateFooter() {
  return new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          text('Page ', { size: FONT_SIZES.tiny, color: COLORS.gray }),
          new TextRun({ children: [PageNumber.CURRENT], font: MONO_FONT, size: FONT_SIZES.tiny, color: COLORS.gray }),
          text(' of ', { size: FONT_SIZES.tiny, color: COLORS.gray }),
          new TextRun({ children: [PageNumber.TOTAL_PAGES], font: MONO_FONT, size: FONT_SIZES.tiny, color: COLORS.gray }),
          text(`    |    Generated: ${new Date().toLocaleString()}`, { size: FONT_SIZES.tiny, color: COLORS.gray })
        ]
      })
    ]
  });
}

// ============================================================================
// MAIN EXPORT FUNCTIONS
// ============================================================================

/**
 * Generate a session analysis report as a Blob
 * @param {Object} options - Report generation options
 * @returns {Promise<Blob>} - The document blob
 */
export async function generateSessionReport(options) {
  const {
    session,
    project,
    projectConfig,
    segments = [],
    events = [],
    sections = {
      header: true,
      settings: true,
      summary: true,
      rollingStraitedge: true,
      iriIntervals: true,
      alr: true
    }
  } = options;
  
  // Check for reverse stationing
  const isReversed = session?.meta?.is_reverse_stationing === true;
  
  // Build document content
  const children = [];
  
  if (sections.header) {
    children.push(...generateHeader(session, project, projectConfig));
  }
  
  if (sections.settings) {
    children.push(...generateSettingsSection(session, projectConfig));
  }
  
  if (sections.summary) {
    children.push(...generateSummarySection(session, segments, events, projectConfig, isReversed));
  }
  
  if (sections.rollingStraitedge) {
    children.push(...generateRollingStraitedgeSection(events, isReversed));
  }
  
  if (sections.iriIntervals) {
    children.push(...generateIRIIntervalsSection(segments, projectConfig, isReversed));
  }
  
  if (sections.alr) {
    children.push(...generateALRSection(events, projectConfig, isReversed));
  }
  
  // Create document with styles
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: MONO_FONT, size: FONT_SIZES.normal }
        }
      },
      paragraphStyles: [
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: FONT_SIZES.heading1, bold: true, font: MONO_FONT, color: COLORS.darkGray },
          paragraph: { spacing: { before: 300, after: 150 }, outlineLevel: 0 }
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: FONT_SIZES.heading2, bold: true, font: MONO_FONT, color: COLORS.darkGray },
          paragraph: { spacing: { before: 200, after: 100 }, outlineLevel: 1 }
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          margin: { top: 1008, right: 1008, bottom: 1008, left: 1008 } // 0.7 inch margins
        }
      },
      footers: {
        default: generateFooter()
      },
      children
    }]
  });
  
  // Generate blob for browser
  return await Packer.toBlob(doc);
}

/**
 * Generate filename for the report
 */
export function generateReportFilename(session, format = 'docx') {
  const sessionName = (session?.name || session?.filename || 'session')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  return `${sessionName}_Report_${date}.${format}`;
}

/**
 * Download the report blob as a file
 */
export function downloadReport(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default generateSessionReport;